import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';

import {
    Anchor,
    Heading,
    Label,
    FormField,
    Select,
    Spacer,
    IconChevronDown,
} from '@zillow/constellation';

import RoomForRent from 'app/shared/components/Modal/AddPropertyModal/components/RoomForRent';
import PropertyConstants from 'app/shared/constants/PropertyConstants';

import {
    StyledTextButton,
    StyledAccordion,
    StyledPanel,
} from './StyledComponents';

const { PROPERTY_TYPES: { CONDO, HOUSE, TOWNHOUSE } } = PropertyConstants;

const propTypes = {
    setCorrectPropertyType: PropTypes.func.isRequired,
    setHasUpdatePropertyTypeError: PropTypes.func.isRequired,
    correctPropertyType: PropTypes.string.isRequired,
};

const EditPropertyTypeModal = ({
    setHasUpdatePropertyTypeError,
    setCorrectPropertyType,
    correctPropertyType,
}) => {
    const { listingType } = useSelector(state => state.properties.propertyOverview);

    const isRoomForRent = listingType.code === 5;

    /* eslint-disable max-len */
    const [accordionData, setAccordianData] = useState([
        {
            title: 'Need to list an entire apartment community?',
            content: "If this property always has availability, you can list it as an apartment community. If you'd prefer to list units individually, create a separate property for each unit.",
            linkText: 'Options for advertising multifamily properties',
            linkHref: 'https://www.zillow.com/z/rental-property-advertising/',
            show: false,
        },
        {
            title: 'Published listing shows wrong property type?',
            content: "If you've set the correct property type in Zillow Rental Manager, but the published listing still shows the wrong type, the type you selected may have been overwritten by public records data.",
            show: false,
        },
    ]);
    /* eslint-enable max-len */

    const handleAccordionToggle = (index) => {
        const accordionCopy = cloneDeep(accordionData);
        accordionCopy[index].show = !accordionCopy[index].show;

        setAccordianData(accordionCopy);
    };

    const handleSelectChange = ({ target: { value } }) => {
        setHasUpdatePropertyTypeError(false);
        setCorrectPropertyType(value);
    };

    return (
        <>
            <Heading level={4} fontFamily="serif">Select the correct property type</Heading>
            <FormField paddingY={1.5}>
                <Label>Property type</Label>
                <Select
                    aria-label="Property type"
                    value={correctPropertyType}
                    onChange={handleSelectChange}
                >
                    <option value={HOUSE}>{HOUSE}</option>
                    <option value={CONDO}>{CONDO}</option>
                    <option value={TOWNHOUSE}>{TOWNHOUSE}</option>
                </Select>
            </FormField>
            {isRoomForRent && (
                <RoomForRent
                    isRoomForRentChecked={isRoomForRent}
                    isRoomForRentDisabled={true}
                />
            )}
            {accordionData.map((dataPoint, i) => (
                <StyledAccordion
                    marginY="xs"
                    paddingTop="xs"
                    key={`accordion-${i}`}
                >
                    <StyledTextButton onClick={() => handleAccordionToggle(i)} rotateIcon={dataPoint.show}>
                        <IconChevronDown />
                        {dataPoint.title}
                    </StyledTextButton>
                    <StyledPanel paddingLeft="md" show={dataPoint.show}>
                        {dataPoint.content}
                        {dataPoint.linkText && (
                            <Spacer marginY="sm">
                                <Anchor href={dataPoint.linkHref} target="_blank">{dataPoint.linkText}</Anchor>
                            </Spacer>
                        )}
                    </StyledPanel>
                </StyledAccordion>
            ))}
        </>
    );
};

EditPropertyTypeModal.propTypes = propTypes;

export default EditPropertyTypeModal;
